import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Paper, Button, Alert, CircularProgress } from '@mui/material';
import { database } from '../config/firebase';
import { ref, get } from 'firebase/database';
import { fixMissingPhoneToGroupEntries, validatePhoneToGroupIntegrity } from '../utils/fixPhoneToGroup';

const TestPhoneToGroup: React.FC = () => {
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [validationResult, setValidationResult] = useState<{ isValid: boolean; issues: string[] } | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      // phoneToGroup 테이블 전체 데이터 로드
      const phoneToGroupRef = ref(database, 'phoneToGroup');
      const phoneToGroupSnapshot = await get(phoneToGroupRef);
      const phoneToGroup = phoneToGroupSnapshot.val() || {};

      // accountGroups 테이블 전체 데이터 로드
      const accountGroupsRef = ref(database, 'accountGroups');
      const accountGroupsSnapshot = await get(accountGroupsRef);
      const accountGroups = accountGroupsSnapshot.val() || {};

      setData({
        phoneToGroup,
        accountGroups,
      });
      
      setMessage({ type: 'success', text: '데이터를 성공적으로 로드했습니다.' });
    } catch (error) {
      console.error('데이터 로드 실패:', error);
      setMessage({ type: 'error', text: '데이터 로드에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleFixMissing = async () => {
    setLoading(true);
    setMessage(null);
    try {
      await fixMissingPhoneToGroupEntries();
      await loadData(); // 데이터 다시 로드
      setMessage({ type: 'success', text: 'phoneToGroup 엔트리가 성공적으로 수정되었습니다.' });
    } catch (error) {
      console.error('수정 실패:', error);
      setMessage({ type: 'error', text: '수정 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const result = await validatePhoneToGroupIntegrity();
      setValidationResult(result);
      if (result.isValid) {
        setMessage({ type: 'success', text: '데이터 무결성 검증을 통과했습니다!' });
      } else {
        setMessage({ type: 'error', text: `${result.issues.length}개의 무결성 문제가 발견되었습니다.` });
      }
    } catch (error) {
      console.error('검증 실패:', error);
      setMessage({ type: 'error', text: '무결성 검증 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          phoneToGroup 테이블 관리
        </Typography>
        
        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Button 
            onClick={loadData} 
            variant="contained" 
            sx={{ mr: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : '데이터 새로고침'}
          </Button>
          
          <Button 
            onClick={handleValidate} 
            variant="contained" 
            color="info" 
            sx={{ mr: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : '무결성 검증'}
          </Button>
          
          <Button 
            onClick={handleFixMissing} 
            variant="contained" 
            color="warning"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : '누락 엔트리 수정'}
          </Button>
        </Box>

        {validationResult && !validationResult.isValid && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light' }}>
            <Typography variant="h6" color="error">무결성 문제:</Typography>
            <ul>
              {validationResult.issues.map((issue, index) => (
                <li key={index}>{issue}</li>
              ))}
            </ul>
          </Paper>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">phoneToGroup 테이블 ({Object.keys(data.phoneToGroup || {}).length}개):</Typography>
          <pre style={{ overflow: 'auto', fontSize: '12px', maxHeight: '400px' }}>
            {JSON.stringify(data.phoneToGroup, null, 2)}
          </pre>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">accountGroups 테이블 ({Object.keys(data.accountGroups || {}).length}개):</Typography>
          <pre style={{ overflow: 'auto', fontSize: '12px', maxHeight: '400px' }}>
            {JSON.stringify(data.accountGroups, null, 2)}
          </pre>
        </Paper>
      </Box>
    </Container>
  );
};

export default TestPhoneToGroup;